'use es6';

import { Map as ImmutableMap, Iterable } from 'immutable';
import { debounce, isEqual, throttle } from 'underscore';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isSamePosition, isPositionWithinBounds, doBoundsIntersect, doOffsetAndBoundsIntersect } from 'layout-dnd-utils/geometry/basic';
import { getCurrentViewportBounds, getElementBounds } from 'layout-dnd-utils/dom/bounds';
import { getCalculateHoverPositionsCalculator } from 'InpageEditorUI/redux/selectors/hoverPositionConfigSelectors';
import { previewScrollPositionChanged } from 'ContentEditorUI/redux/actions/inpageActions';
import { getWidgetsInRichText } from 'ContentEditorUI/redux/selectors/moduleSelectorHelpers';
import { dragHandlesBlurred, hoveredDragHandleUpdated, layoutSectionHoverPositionsUpdated, layoutSectionNodePositionsUpdated, layoutSectionNodePositionsRefreshedButUnchanged, mouseEnteredIFrame, mouseLeftIFrame, iframeScrollPositionChanged } from 'InpageEditorUI/redux/actions/layoutSectionDndActions';
import { getLayoutIteratorMap, getGridLayoutIteratorMap } from 'ContentEditorUI/redux/selectors/layoutDataSelectors';
import { getTargetedOrNearbyLayoutSectionId, getCurrentDragItem, getCurrentModuleToolbarInfo, getCurrentEmptyContainerToolbarInfo, getCurrentRowToolbarInfo, getCurrentTopmostRowToolbarInfo, getCurrentColumnToolbarInfo, getCurrentVerticalStackToolbarInfo, getCurrentHorizontalStackToolbarInfo, getCurrentColumnResizeHandleInfo, getCurrentInsertNewSectionToolbarInfo, getMouseCoordsAtDrop, getNodePositions, getNodePositionsByTreeId, getNodePositionsByConfigAndId, getNodePositionsNeedUpdate, getHoverPositionsFrozen, getScrollLeftPosition, getScrollTopPosition, getBodyOffset as getBodyOffsetSelector, getSelectedItemPositionInfo, getCurrentGridTableToolbarInfo } from 'InpageEditorUI/redux/selectors/layoutSectionDndSelectors';
import { getAllLayoutSectionIds, getAllLayoutSectionTreesAsMap, getAllStaticModulesToShowOverlaysOn, getAllFlexColumnModules, getModulesFromPostBody, getAllGridLayoutIds, getAllGridLayoutTreesAsMap, getIsEditingGridLayouts } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import PerformanceStatsGatherer from 'ContentEditorUI/lib/PerformanceStatsGatherer';
import { getDocumentBodyOffset } from 'layout-dnd-utils/dom/offset';
import shallowEqual from 'ContentEditorUI/utils/shallowEqual';
import { resizeHandleDimensions, STATIC_MODULES_CONFIG, FLEX_COLUMN_MODULES_CONFIG, GLOBAL_GROUPS_CONFIG, GLOBAL_PARTIALS_CONFIG, HEADER_INFO_CONFIG, FOOTER_INFO_CONFIG, LAYOUT_SECTIONS_CONFIG, POST_BODY_MODULES_CONFIG, NESTED_MODULES_IN_RTE_CONFIG, GRID_LAYOUTS_NODE_POSITION_CONFIG } from 'InpageEditorUI/lib/layoutSection/constants';
import { getGlobalGroupInfo, getGlobalPartialInfo, getHeaderInfo, getFooterInfo } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import { incrementalSearchForChangedBounds, basicCancelAndClearAllDeferredTimers } from 'layout-dnd-utils/nodePositions/incrementalSearchForChangedBounds';
import { getNodePositionConfig, getCalculateNodePositionsFunc } from 'InpageEditorUI/redux/selectors/nodePositionConfigSelectors';
import { getIsDistractionFreeOpen } from 'ContentEditorUI/redux/selectors/localCachedSettingsSelectors';
import { DISTRACTION_FREE_CLASSNAME } from 'ContentEditorUI/constants/dom';
import { getGridLayoutDataTreeIterator } from 'layout-data-lib/GridLayoutDataTree/utils';
import memoizeOne from 'react-utils/memoizeOne';
const getNodePositionsAreFrozen = state => state.nodePositionsFrozen;
class NodePositionContainer extends Component {
  constructor(props) {
    super(props);
    this.throttledHover = throttle(this.onHover.bind(this), 0);
    this.throttledCheckIfNodePositionsShouldUpdate = throttle(this.checkIfNodePositionsShouldUpdate.bind(this), 1000);
    this.debouncedUpdateNodePositions = debounce(this.updateNodePositions.bind(this), 300);
    this.debouncedUpdateScrollPosition = debounce(this.updateScrollPosition.bind(this), 300);
    this.startCheckIfNodePositionsShouldUpdateObj = {};
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.onNewPositionFromIncrementalSearch = this.onNewPositionFromIncrementalSearch.bind(this);
    this.onFinishIncrementalSearchWithNoChange = this.onFinishIncrementalSearchWithNoChange.bind(this);
    this.perfStats = PerformanceStatsGatherer.getSingletonInstance();
    this.forcedMouseCoordinates = null;
    this.tourCallback = this.tourCallback.bind(this);
    this.updateBlockedByFrozenNodePositions = false;
    this.checkShouldUpdateBlockedByFrozenNodePositions = false;
  }
  componentDidMount() {
    const {
      nodePositionsNeedUpdate
    } = this.props;
    const {
      tour
    } = this.context;
    this.attachEventListeners();
    this.updateScrollPosition();
    if (nodePositionsNeedUpdate) {
      this.updateNodePositions();
    }
    tour.subscribe(this.tourCallback);
  }
  componentDidUpdate(prevProps) {
    const {
      currentDragItem,
      currentNodePositions,
      nodePositionsNeedUpdate,
      hoverPositionsFrozen,
      mouseCoordsAtDrop,
      allStaticModules,
      allFlexColumnModules,
      allPostBodyModules,
      allLayoutSectionTrees,
      nodePositionsFrozen,
      widgetsInRichText
    } = this.props;
    if (prevProps.nodePositionsFrozen && !nodePositionsFrozen) {
      if (this.updateBlockedByFrozenNodePositions) {
        this.updateNodePositions();
        this.updateBlockedByFrozenNodePositions = false;
        this.checkShouldUpdateBlockedByFrozenNodePositions = false;
      } else if (this.checkShouldUpdateBlockedByFrozenNodePositions) {
        this.checkIfNodePositionsShouldUpdate();
        this.checkShouldUpdateBlockedByFrozenNodePositions = false;
      }
    }
    const nodePositionsChanged = currentNodePositions !== prevProps.currentNodePositions;
    const anyModuleOrTreeChange = prevProps.allStaticModules !== allStaticModules || prevProps.allFlexColumnModules !== allFlexColumnModules || prevProps.allLayoutSectionTrees !== allLayoutSectionTrees || prevProps.allPostBodyModules !== allPostBodyModules || prevProps.widgetsInRichText !== widgetsInRichText;
    if (nodePositionsNeedUpdate) {
      this.updateNodePositions();
    } else if (anyModuleOrTreeChange) {
      // Optimistically schedule a new node positions "check" after module content or styles change
      // (instead of waiting until the customer moves the mouse over the iframe)
      this.throttledCheckIfNodePositionsShouldUpdate();
    }
    if (prevProps.currentDragItem && !currentDragItem) {
      this.needToUpdateHoverPositionsPostDrop = true;
    }
    if (this.previousMouseCoordinates && hoverPositionsFrozen === false && prevProps.hoverPositionsFrozen) {
      this.forceUpdateHoverPositions(this.previousMouseCoordinates);
    }
    // If a drop happened _and_ the `updateNodePositions` dispatch has fully "gone through" use the
    // mouse coordinate of the drop to render new hover positions (repositioning any overlays,
    // handles and toolbars).
    else if (this.needToUpdateHoverPositionsPostDrop && nodePositionsChanged && !nodePositionsNeedUpdate && Object.keys(mouseCoordsAtDrop).length > 0) {
      this.forceUpdateHoverPositions(mouseCoordsAtDrop);
    }

    // But if the structure changed and this wasn't just a drop or a current drag, force hover
    // positions to update with the last known mouse coordinates
    else if (!currentDragItem && this.previousMouseCoordinates && !nodePositionsNeedUpdate && (nodePositionsChanged || this.hasAnyHandleInfoChanged(prevProps))) {
      this.forceUpdateHoverPositions(this.previousMouseCoordinates);
    }
  }
  componentWillUnmount() {
    this.detachEventListeners();
  }
  tourCallback(stepKey) {
    if (stepKey === 'resizeColumn') {
      this.detachEventListeners();
      this.forcedMouseCoordinates = this.getForcedMouseCoordinatesForModuleForOnboardingTour();
    } else if (stepKey === 'sectionDropdown') {
      this.forcedMouseCoordinates = this.getForcedMouseCoordinatesForSectionForOnboardingTour();
    } else {
      this.forcedMouseCoordinates = null;
      this.attachEventListeners();
    }
  }
  attachEventListeners() {
    document.addEventListener('mousemove', this.throttledHover, {
      capture: true
    });
    window.addEventListener('resize', this.debouncedUpdateNodePositions);
    window.addEventListener('scroll', this.debouncedUpdateScrollPosition);
    document.addEventListener('mouseleave', this.handleMouseLeave);
    document.addEventListener('mouseenter', this.handleMouseEnter);
    document.addEventListener('dragenter', this.handleMouseEnter);
    document.addEventListener('dragleave', this.handleMouseEnter);
  }
  detachEventListeners() {
    document.removeEventListener('mousemove', this.throttledHover, {
      capture: true
    });
    window.removeEventListener('resize', this.debouncedUpdateNodePositions);
    window.removeEventListener('scroll', this.debouncedUpdateScrollPosition);
    document.removeEventListener('mouseleave', this.handleMouseLeave);
    document.removeEventListener('mouseenter', this.handleMouseEnter);
    document.removeEventListener('dragenter', this.handleMouseEnter);
    document.removeEventListener('dragleave', this.handleMouseEnter);
  }
  handleMouseLeave() {
    this.props.mouseLeftIFrame();
  }
  handleMouseEnter() {
    this.props.mouseEnteredIFrame();
  }

  // Basic handle comparison, but only checking the ID and type (no need to check every key,
  // since node position updates will force rerendering anway)
  isHandleDifferent(prevHandle, newHandle) {
    if (prevHandle === newHandle) {
      return false;
    } else if (prevHandle && !newHandle || !prevHandle && newHandle) {
      return true;
    }
    return prevHandle.id !== newHandle.id || prevHandle.type !== newHandle.type;
  }

  // Basic resize handle array comparison. Just like `isHandleDifferent` only comparing necessary
  // properties instead of doing a full, excessive check of all of the properties that are included on
  // the resize objects
  areResizeHandlesDifferent(prevResizeInfoArray, resizeInfoArray) {
    if (prevResizeInfoArray === resizeInfoArray) {
      return false;
    } else if (prevResizeInfoArray && !resizeInfoArray || !prevResizeInfoArray && resizeInfoArray) {
      return true;
    } else if (prevResizeInfoArray.length !== resizeInfoArray.length) {
      return true;
    }
    return prevResizeInfoArray.some((prev, i) => {
      const next = resizeInfoArray[i];
      const prevLeftId = prev.leftColumn && prev.leftColumn.id;
      const nextLeftId = next.leftColumn && next.leftColumn.id;
      const prevRightId = prev.rightColumn && prev.rightColumn.id;
      const nextRightId = next.rightColumn && next.rightColumn.id;
      return prev.type !== next.type || prevLeftId !== nextLeftId || prevRightId !== nextRightId;
    });
  }
  hasAnyHandleInfoChanged(prevProps) {
    const {
      currentModuleToolbarInfo,
      currentEmptyContainerToolbarInfo,
      currentColumnResizeHandleInfo,
      currentRowToolbarInfo,
      currentColumnToolbarInfo,
      currentTopmostRowToolbarInfo,
      currentInsertNewSectionToolbarInfo,
      currentVerticalStackToolbarInfo,
      currentHorizontalStackToolbarInfo,
      currentGridSectionToolbarInfo,
      currentGridTableToolbarInfo
    } = this.props;
    return this.isHandleDifferent(prevProps.currentModuleToolbarInfo, currentModuleToolbarInfo) || this.isHandleDifferent(prevProps.currentEmptyContainerToolbarInfo, currentEmptyContainerToolbarInfo) || this.isHandleDifferent(prevProps.currentRowToolbarInfo, currentRowToolbarInfo) || this.isHandleDifferent(prevProps.currentColumnToolbarInfo, currentColumnToolbarInfo) || this.isHandleDifferent(prevProps.currentTopmostRowToolbarInfo, currentTopmostRowToolbarInfo) || this.isHandleDifferent(prevProps.currentInsertNewSectionToolbarInfo, currentInsertNewSectionToolbarInfo) || this.isHandleDifferent(prevProps.currentVerticalStackToolbarInfo, currentVerticalStackToolbarInfo) || this.isHandleDifferent(prevProps.currentGridSectionToolbarInfo, currentGridSectionToolbarInfo) || this.isHandleDifferent(prevProps.currentHorizontalStackToolbarInfo, currentHorizontalStackToolbarInfo) || this.isHandleDifferent(prevProps.currentGridTableToolbarInfo, currentGridTableToolbarInfo) || this.areResizeHandlesDifferent(prevProps.currentColumnResizeHandleInfo, currentColumnResizeHandleInfo);
  }
  checkIfToolbarIsHovered(handleInfo, mouseCoordinates) {
    // For transformed toolbars, use the "perceivedPosition" when comparing to mouse coordinates
    const {
      top: perceivedTop,
      left: perceivedLeft
    } = handleInfo.perceivedPosition || handleInfo;
    const bounds = {
      top: perceivedTop,
      left: perceivedLeft,
      bottom: perceivedTop + handleInfo.height,
      right: perceivedLeft + handleInfo.width
    };
    if (isPositionWithinBounds(mouseCoordinates, bounds)) {
      this.props.hoveredDragHandleUpdated(handleInfo.type, handleInfo.id);
      this.handleHovered = handleInfo.type;
    }
  }
  checkIfAnyResizeHandlesAreHovered(resizeHandleInfo, mouseCoordinates) {
    for (let i = 0; i < resizeHandleInfo.length; i++) {
      const thisResizeHandle = resizeHandleInfo[i];
      const gutterBounds = {
        top: thisResizeHandle.gutterTop,
        left: thisResizeHandle.gutterLeft,
        right: thisResizeHandle.gutterLeft + thisResizeHandle.gutterWidth,
        bottom: thisResizeHandle.gutterTop + thisResizeHandle.gutterHeight
      };
      if (isPositionWithinBounds(mouseCoordinates, gutterBounds)) {
        this.props.hoveredDragHandleUpdated(thisResizeHandle.type);
        this.handleHovered = thisResizeHandle.type;
        break;
      }

      // In cases of small gutters, the handle could be wider than the gutter itself, so check if we are hovered over that if the above check fails
      const handleBounds = {
        top: gutterBounds.top + thisResizeHandle.handleTop,
        left: gutterBounds.left + thisResizeHandle.handleLeft,
        right: gutterBounds.left + thisResizeHandle.handleLeft + resizeHandleDimensions.WIDTH,
        bottom: gutterBounds.bottom + thisResizeHandle.handleTop + thisResizeHandle.handleHeight
      };
      if (isPositionWithinBounds(mouseCoordinates, handleBounds)) {
        this.props.hoveredDragHandleUpdated(thisResizeHandle.type);
        this.handleHovered = thisResizeHandle.type;
        break;
      }
    }
  }
  forceUpdateHoverPositions(mouseCoordinates) {
    this.previousHoverPositions = null;
    this.needToUpdateHoverPositionsPostDrop = false;
    this.updateHoverPositionsIfNeeded(mouseCoordinates);
  }
  getForcedMouseCoordinatesForModuleForOnboardingTour() {
    const {
      currentNodePositions
    } = this.props;
    const keys = Object.keys(currentNodePositions[LAYOUT_SECTIONS_CONFIG]);
    // We force the user to choose a template with two layout sections, and
    // here we want to pick the main content one, so we look for the first section label
    // that doesn't say footer. The main content section has a consistent name, but
    // finding it this way is defensive in case we decide to change the template used
    // in the tour, or if the name of the main section changes on the backend
    const key = keys.find(k => !k.includes('footer'));
    // This finds the deepest nested module in the section, which ensures that
    // the resize handle is displayed since it's within a column, within a row
    const deepestNode = currentNodePositions[LAYOUT_SECTIONS_CONFIG][key][currentNodePositions[LAYOUT_SECTIONS_CONFIG][key].length - 1];
    const forcedTop = deepestNode.top + deepestNode.height / 2;
    const forcedLeft = deepestNode.left + deepestNode.width / 2;
    return {
      y: forcedTop,
      x: forcedLeft
    };
  }
  getForcedMouseCoordinatesForSectionForOnboardingTour() {
    const {
      currentNodePositions
    } = this.props;
    const keys = Object.keys(currentNodePositions[LAYOUT_SECTIONS_CONFIG]);
    // We force the user to choose a template with two layout sections, and
    // here we want to pick the main content one, so we look for the first section label
    // that doesn't say footer. The main content section has a consistent name, but
    // finding it this way is defensive in case we decide to change the template used
    // in the tour, or if the name of the main section changes on the backend
    const key = keys.find(k => !k.includes('footer'));
    // This find the data for the least nested node, the section itself
    const section = currentNodePositions[LAYOUT_SECTIONS_CONFIG][key][0];
    const forcedTop = section.top;
    const forcedLeft = section.left;
    return {
      y: forcedTop,
      x: forcedLeft
    };
  }
  getEffectiveViewportBounds() {
    const distractionFreeWrapper = document.querySelector(`.${DISTRACTION_FREE_CLASSNAME}`);
    if (distractionFreeWrapper) {
      return getElementBounds(distractionFreeWrapper, {
        applyPositionInfo: true,
        applyElementScrollOffset: true
      });
    }
    return getCurrentViewportBounds();
  }
  updateHoverPositionsIfNeeded(mouseCoordinates) {
    const {
      currentNodePositions,
      currentModuleToolbarInfo,
      currentEmptyContainerToolbarInfo,
      currentColumnResizeHandleInfo,
      currentRowToolbarInfo,
      currentColumnToolbarInfo,
      currentTopmostRowToolbarInfo,
      currentInsertNewSectionToolbarInfo,
      currentVerticalStackToolbarInfo,
      currentHorizontalStackToolbarInfo,
      currentGridSectionToolbarInfo,
      currentGridTableToolbarInfo,
      nodePositionsNeedUpdate,
      hoverPositionsFrozen,
      selectedItemPositionInfo
    } = this.props;
    mouseCoordinates = this.forcedMouseCoordinates || mouseCoordinates;
    if (!nodePositionsNeedUpdate && !hoverPositionsFrozen && !isSamePosition(mouseCoordinates, this.previousMouseCoordinates)) {
      const startHandleBoundsChecks = this.perfStats.now();
      this.needToUpdateHoverPositionsPostDrop = false;
      this.handleHovered = null;

      // Row handles can overhang column gutters, in those instances
      // we want the row handles to "win"
      if (!this.handleHovered && currentRowToolbarInfo) {
        this.checkIfToolbarIsHovered(currentRowToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentColumnToolbarInfo) {
        this.checkIfToolbarIsHovered(currentColumnToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentVerticalStackToolbarInfo) {
        this.checkIfToolbarIsHovered(currentVerticalStackToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentHorizontalStackToolbarInfo) {
        this.checkIfToolbarIsHovered(currentHorizontalStackToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentGridSectionToolbarInfo) {
        this.checkIfToolbarIsHovered(currentGridSectionToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentGridTableToolbarInfo) {
        this.checkIfToolbarIsHovered(currentGridTableToolbarInfo, mouseCoordinates);
      }

      // The insert new section handle can overlap the column resize handle sometimes, in those
      // cases, we want the insert new section handle to win.
      if (!this.handleHovered && currentInsertNewSectionToolbarInfo) {
        this.checkIfToolbarIsHovered(currentInsertNewSectionToolbarInfo, mouseCoordinates);
      }

      // When there is small or no gutters, the resize handle could overhang the module,
      // in those cases we want the resize handle to "win" so the users can still resize columns
      if (!this.handleHovered && currentColumnResizeHandleInfo) {
        this.checkIfAnyResizeHandlesAreHovered(currentColumnResizeHandleInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentModuleToolbarInfo) {
        this.checkIfToolbarIsHovered(currentModuleToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentEmptyContainerToolbarInfo) {
        this.checkIfToolbarIsHovered(currentEmptyContainerToolbarInfo, mouseCoordinates);
      }
      if (!this.handleHovered && currentTopmostRowToolbarInfo) {
        this.checkIfToolbarIsHovered(currentTopmostRowToolbarInfo, mouseCoordinates);
      }
      this.perfStats.incrementCounterAndMeasure('seeIfAnyHandlesAreHovered', this.perfStats.now() - startHandleBoundsChecks);
      if (!this.handleHovered) {
        if (this.previousHandleHovered) {
          this.props.dragHandlesBlurred();
        }
        const startCalculateHoverPositions = this.perfStats.now();
        this.hoverPositions = this.props.calculateHoverPositions(currentNodePositions, mouseCoordinates, {
          includeOutsideBuffer: false,
          selectedItemPositionInfo
        });
        this.perfStats.incrementCounterAndMeasure('calculateHoverPositions', this.perfStats.now() - startCalculateHoverPositions);
        if (!isEqual(this.hoverPositions, this.previousHoverPositions)) {
          this.perfStats.incrementCounter('layoutSectionHoverPositionsUpdated');
          this.props.layoutSectionHoverPositionsUpdated(this.hoverPositions);
        }
      }
    }
    this.previousMouseCoordinates = mouseCoordinates;
    this.previousHoverPositions = this.hoverPositions;
    this.previousHandleHovered = this.handleHovered;
  }
  onHover(event) {
    const mouseCoordinates = this.forcedMouseCoordinates || {
      x: event.pageX,
      y: event.pageY,
      clientX: event.clientX,
      clientY: event.clientY
    };
    this.perfStats.incrementCounter('onHover');
    this.perfStats.startCollectingReactMeasurements();

    // Every time it has been more than a second since the last position check, do another
    this.throttledCheckIfNodePositionsShouldUpdate();
    this.updateHoverPositionsIfNeeded(mouseCoordinates);
    this.perfStats.stopCollectingReactMeasurements();
  }
  incrementalSearchForChangedBoundsOnUniqueModules({
    modulesList,
    nodePositionSubConfig,
    previousNodePositions,
    currentViewportBounds,
    isDistractionFreeOpen
  }) {
    if (nodePositionSubConfig && previousNodePositions) {
      const moduleAndBoundsTuplesToCheck = [];
      this.onStartNewIncrementalSearch({
        nodePositionSubConfig
      });
      const effectiveViewportBounds = isDistractionFreeOpen && this.getEffectiveViewportBounds();
      modulesList.forEach(module => {
        const existingNodePosition = previousNodePositions[module.get('name')];
        if (!existingNodePosition) {
          return;
        }
        const boundsIntersect = doBoundsIntersect(existingNodePosition, currentViewportBounds);
        const offsetAndBoundsIntersect = effectiveViewportBounds && existingNodePosition.hasOffset && doOffsetAndBoundsIntersect(existingNodePosition, effectiveViewportBounds);
        if (boundsIntersect || offsetAndBoundsIntersect) {
          moduleAndBoundsTuplesToCheck.push([module, existingNodePosition]);
        }
      });
      incrementalSearchForChangedBounds(moduleAndBoundsTuplesToCheck, 0, {
        nodePositionSubConfig,
        onPositionChanged: this.onNewPositionFromIncrementalSearch,
        onFinishWithNoChange: this.onFinishIncrementalSearchWithNoChange
      });
    }
  }
  incrementalSearchForChangedBoundsOnNonUniqueStaticGlobals({
    globals,
    nodePositionSubConfig,
    previousGlobalNodePositions,
    currentViewportBounds
  }) {
    if (nodePositionSubConfig && previousGlobalNodePositions) {
      const globalAndBoundsTuplesToCheck = [];
      this.onStartNewIncrementalSearch({
        nodePositionSubConfig
      });

      // Iterating over existing positions (not actual global group data) first since multiple
      // global groups can be from the same path (i.e. same non-unique id)
      Object.values(globals).forEach(globalGroup => {
        const existingNodePositionsSortedByIndex = Object.values(previousGlobalNodePositions).filter(groupPosition => groupPosition.path === globalGroup.path)
        // Sort by increasing index (1, 2, 3, ...)
        .sort((positionA, positionB) => positionA.index - positionB.index);
        const anyPositionBoundsAreOnScreen = existingNodePositionsSortedByIndex.some(groupPosition => doBoundsIntersect(groupPosition, currentViewportBounds));
        if (anyPositionBoundsAreOnScreen) {
          globalAndBoundsTuplesToCheck.push([globalGroup, ...existingNodePositionsSortedByIndex]);
        }
      });
      incrementalSearchForChangedBounds(globalAndBoundsTuplesToCheck, 0, {
        nodePositionSubConfig,
        onPositionChanged: this.onNewPositionFromIncrementalSearch,
        onFinishWithNoChange: this.onFinishIncrementalSearchWithNoChange
      });
    }
  }
  incrementalSearchForChangedBoundsInDndArea({
    layoutSectionTreeToCheck,
    nodePositionsForLayoutSectionToCheck,
    layoutSectionNodePositionConfig,
    currentViewportBounds
  }) {
    const columnAndBoundTuplesToCheck = [];
    this.onStartNewIncrementalSearch({
      nodePositionSubConfig: layoutSectionNodePositionConfig
    });
    layoutSectionTreeToCheck.iterate(({
      cell
    }) => {
      if (cell) {
        const existingNodePosition = nodePositionsForLayoutSectionToCheck[cell.getName()];
        if (existingNodePosition && doBoundsIntersect(existingNodePosition, currentViewportBounds)) {
          columnAndBoundTuplesToCheck.push([cell, existingNodePosition]);
        } else {
          // Stop iterating this branch of the layout tree
          // assume all children are also not in viewport
          return false;
        }
      }
      return true;
    });

    // Sort by depth so we can look at the bigger elements first, then work down
    columnAndBoundTuplesToCheck.sort((a, b) => {
      return a.depth - b.depth;
    });

    // Start walking the layout section tree breadth first
    incrementalSearchForChangedBounds(columnAndBoundTuplesToCheck, 0, {
      nodePositionSubConfig: layoutSectionNodePositionConfig,
      onPositionChanged: this.onNewPositionFromIncrementalSearch,
      onFinishWithNoChange: this.onFinishIncrementalSearchWithNoChange
    });
  }
  incrementalSearchForChangedBoundsInGridLayout({
    gridLayoutToCheck,
    nodePositionsForGridLayoutToCheck,
    gridLayoutNodePositionConfig,
    currentViewportBounds
  }) {
    const gridNodeAndBoundTuplesToCheck = [];
    const gridLayoutDataTreeIterator = getGridLayoutDataTreeIterator(gridLayoutToCheck);
    gridLayoutDataTreeIterator({
      gridNodeCallback: ({
        gridNode
      }) => {
        const existingNodePosition = nodePositionsForGridLayoutToCheck[gridNode.getId()];
        if (existingNodePosition && doBoundsIntersect(existingNodePosition, currentViewportBounds)) {
          gridNodeAndBoundTuplesToCheck.push([gridNode, existingNodePosition]);
        } else {
          // TODO: Replace with separate util or update current one to support
          // the below:
          // Stop iterating this branch of the layout tree
          // assume all children are also not in viewport
          return false;
        }
        return true;
      }
    });

    // Sort by depth so we can look at the bigger elements first, then work down
    gridNodeAndBoundTuplesToCheck.sort((a, b) => {
      return a[1].depth - b[1].depth;
    });

    // Start walking the layout section tree breadth first
    incrementalSearchForChangedBounds(gridNodeAndBoundTuplesToCheck, 0, {
      nodePositionSubConfig: gridLayoutNodePositionConfig,
      onPositionChanged: this.onNewPositionFromIncrementalSearch,
      onFinishWithNoChange: this.onFinishIncrementalSearchWithNoChange
    });
  }

  // Selectily poll for bounds changes for any modules & D&D tree columns/modules onscreen
  checkIfNodePositionsShouldUpdate({
    layoutSectionIdToCheck
  } = {}) {
    const {
      allPostBodyModules,
      allStaticModules,
      allFlexColumnModules,
      globalGroups,
      globalPartials,
      headerInfo,
      footerInfo,
      targetedLayoutSectionId,
      allLayoutSectionTrees,
      nodePositionConfig,
      nodePositionsByConfigAndId,
      nodePositionsByTreeId,
      nodePositionsFrozen,
      isDistractionFreeOpen,
      widgetsInRichText,
      allGridLayouts,
      isEditingGridLayouts
    } = this.props;
    if (nodePositionsFrozen) {
      this.checkShouldUpdateBlockedByFrozenNodePositions = true;
      return;
    }
    const {
      [STATIC_MODULES_CONFIG]: staticModuleNodePositionConfig,
      [FLEX_COLUMN_MODULES_CONFIG]: flexColumnModuleNodePositionConfig,
      [GLOBAL_GROUPS_CONFIG]: globalGroupNodePositionConfig,
      [GLOBAL_PARTIALS_CONFIG]: globalPartialNodePositionConfig,
      [HEADER_INFO_CONFIG]: headerNodePositionConfig,
      [FOOTER_INFO_CONFIG]: footerNodePositionConfig,
      [LAYOUT_SECTIONS_CONFIG]: layoutSectionNodePositionConfig,
      [POST_BODY_MODULES_CONFIG]: postBodyModulesConfig,
      [NESTED_MODULES_IN_RTE_CONFIG]: nestedModuleInRichTextConfig,
      [GRID_LAYOUTS_NODE_POSITION_CONFIG]: gridLayoutNodePositionConfig
    } = nodePositionConfig;
    const currentViewportBounds = getCurrentViewportBounds();

    // Cancel any deferred bounds checking
    basicCancelAndClearAllDeferredTimers();
    if (nodePositionsByConfigAndId) {
      const staticModuleNodePositions = nodePositionsByConfigAndId[STATIC_MODULES_CONFIG];
      const flexColumnModulePositions = nodePositionsByConfigAndId[FLEX_COLUMN_MODULES_CONFIG];
      const globalGroupPositions = nodePositionsByConfigAndId[GLOBAL_GROUPS_CONFIG];
      const globalPartialPositions = nodePositionsByConfigAndId[GLOBAL_PARTIALS_CONFIG];
      const headerNodePositions = nodePositionsByConfigAndId[HEADER_INFO_CONFIG];
      const footerNodePositions = nodePositionsByConfigAndId[FOOTER_INFO_CONFIG];
      const postBodyModuleNodePositions = nodePositionsByConfigAndId[POST_BODY_MODULES_CONFIG];
      const nestedModuleInRichTextPositions = nodePositionsByConfigAndId[NESTED_MODULES_IN_RTE_CONFIG];

      // Check post modules
      this.incrementalSearchForChangedBoundsOnUniqueModules({
        modulesList: allPostBodyModules,
        nodePositionSubConfig: postBodyModulesConfig,
        previousNodePositions: postBodyModuleNodePositions,
        currentViewportBounds,
        isDistractionFreeOpen
      });

      // Check for nested RTE modules
      this.incrementalSearchForChangedBoundsOnUniqueModules({
        modulesList: Object.values(widgetsInRichText),
        nodePositionSubConfig: nestedModuleInRichTextConfig,
        previousNodePositions: nestedModuleInRichTextPositions,
        currentViewportBounds
      });

      // Check static modules
      this.incrementalSearchForChangedBoundsOnUniqueModules({
        modulesList: Object.values(allStaticModules),
        nodePositionSubConfig: staticModuleNodePositionConfig,
        previousNodePositions: staticModuleNodePositions,
        currentViewportBounds
      });

      // Check flex column modules
      this.incrementalSearchForChangedBoundsOnUniqueModules({
        modulesList: Object.values(allFlexColumnModules),
        nodePositionSubConfig: flexColumnModuleNodePositionConfig,
        previousNodePositions: flexColumnModulePositions,
        currentViewportBounds
      });

      // Check global groups
      this.incrementalSearchForChangedBoundsOnNonUniqueStaticGlobals({
        globals: globalGroups,
        nodePositionSubConfig: globalGroupNodePositionConfig,
        previousGlobalNodePositions: globalGroupPositions,
        currentViewportBounds
      });

      // Check global partials
      this.incrementalSearchForChangedBoundsOnNonUniqueStaticGlobals({
        globals: globalPartials,
        nodePositionSubConfig: globalPartialNodePositionConfig,
        previousGlobalNodePositions: globalPartialPositions,
        currentViewportBounds
      });

      // Check header globals
      this.incrementalSearchForChangedBoundsOnNonUniqueStaticGlobals({
        globals: headerInfo,
        nodePositionSubConfig: headerNodePositionConfig,
        previousGlobalNodePositions: headerNodePositions,
        currentViewportBounds
      });

      // Check footer globals
      this.incrementalSearchForChangedBoundsOnNonUniqueStaticGlobals({
        globals: footerInfo,
        nodePositionSubConfig: footerNodePositionConfig,
        previousGlobalNodePositions: footerNodePositions,
        currentViewportBounds
      });
    }

    // Check D&D areas. By default this only checks the currently hovered D&D area, but also allow a
    // caller to force which area to look for node position updates
    layoutSectionIdToCheck = layoutSectionIdToCheck || targetedLayoutSectionId;
    if (!isEditingGridLayouts && layoutSectionIdToCheck && nodePositionsByTreeId) {
      this.incrementalSearchForChangedBoundsInDndArea({
        layoutSectionTreeToCheck: allLayoutSectionTrees[layoutSectionIdToCheck],
        nodePositionsForLayoutSectionToCheck: nodePositionsByTreeId[layoutSectionIdToCheck],
        layoutSectionNodePositionConfig,
        currentViewportBounds
      });
    }
    if (isEditingGridLayouts && layoutSectionIdToCheck && nodePositionsByTreeId) {
      this.incrementalSearchForChangedBoundsInGridLayout({
        gridLayoutToCheck: allGridLayouts[layoutSectionIdToCheck],
        nodePositionsForGridLayoutToCheck: nodePositionsByTreeId[layoutSectionIdToCheck],
        gridLayoutNodePositionConfig,
        currentViewportBounds
      });
    }
  }
  onStartNewIncrementalSearch({
    nodePositionSubConfig
  }) {
    this.startCheckIfNodePositionsShouldUpdateObj[nodePositionSubConfig.key] = this.perfStats.now();
  }
  onNewPositionFromIncrementalSearch(item, {
    nodePositionSubConfig
  }) {
    if (nodePositionSubConfig.debug) {
      console.log('onNewPositionFromIncrementalSearch', nodePositionSubConfig.key, item);
    }
    this.perfStats.incrementCounterAndMeasure(`checkIfNodePositionsShouldUpdate-${nodePositionSubConfig.key}`, this.perfStats.now() - this.startCheckIfNodePositionsShouldUpdateObj[nodePositionSubConfig.key]);

    // Found a column/cell that is in a new place. Bailing from further checking and going to
    // update all node positions
    this.updateNodePositions();
  }
  onFinishIncrementalSearchWithNoChange({
    nodePositionSubConfig
  }) {
    if (nodePositionSubConfig.debug) {
      console.log('onFinishIncrementalSearchWithNoChange', nodePositionSubConfig.key);
    }
    // Ran out of nodes to check
    this.perfStats.incrementCounterAndMeasure(`checkIfNodePositionsShouldUpdate-${nodePositionSubConfig.key}`, this.perfStats.now() - this.startCheckIfNodePositionsShouldUpdateObj[nodePositionSubConfig.key]);
  }
  updateNodePositions() {
    const {
      allStaticModules,
      allFlexColumnModules,
      allLayoutSectionIds,
      globalGroups,
      globalPartials,
      headerInfo,
      footerInfo,
      layoutIteratorMap,
      currentNodePositions,
      nodePositionsNeedUpdate,
      currentBodyOffset,
      calculateNodePositions,
      allPostBodyModules,
      widgetsInRichText,
      allGridLayoutIds,
      gridLayoutIteratorMap,
      isEditingGridLayouts
    } = this.props;
    if (this.props.nodePositionsFrozen) {
      this.updateBlockedByFrozenNodePositions = true;
      return;
    }
    const nodePositionsInfo = isEditingGridLayouts ? {
      gridLayoutIds: allGridLayoutIds,
      gridLayoutIteratorMap,
      globalGroups: Object.values(globalGroups),
      globalPartials: Object.values(globalPartials)
    } : {
      globalGroups: Object.values(globalGroups),
      globalPartials: Object.values(globalPartials),
      headerInfo: Object.values(headerInfo),
      footerInfo: Object.values(footerInfo),
      layoutSectionIds: allLayoutSectionIds,
      layoutSectionIteratorMap: layoutIteratorMap,
      widgetsInRichText: Object.values(widgetsInRichText),
      allStaticModules: Object.values(allStaticModules),
      allFlexColumnModules: Object.values(allFlexColumnModules),
      allPostBodyModules
    };
    const newNodePositions = calculateNodePositions(nodePositionsInfo, currentNodePositions);
    const newBodyOffset = getDocumentBodyOffset();
    const bodyOffsetChanged = !shallowEqual(currentBodyOffset, newBodyOffset);
    const nodePositionsChanged = currentNodePositions !== newNodePositions;
    if (bodyOffsetChanged || nodePositionsChanged) {
      this.props.layoutSectionNodePositionsUpdated(newNodePositions, newBodyOffset);
    } else if (nodePositionsNeedUpdate) {
      this.props.layoutSectionNodePositionsRefreshedButUnchanged();
    }
  }
  updateScrollPosition() {
    const {
      scrollLeftPosition,
      scrollTopPosition
    } = this.props;
    const newScrollLeft = window.scrollX;
    const newScrollTop = window.scrollY;
    if (scrollLeftPosition !== newScrollLeft || scrollTopPosition !== newScrollTop) {
      this.props.iframeScrollPositionChanged({
        left: newScrollLeft,
        top: newScrollTop
      });
      this.props.previewScrollPositionChanged({
        left: newScrollLeft,
        top: newScrollTop
      });
    }
  }
  render() {
    return null;
  }
}
NodePositionContainer.propTypes = {
  allStaticModules: PropTypes.object.isRequired,
  allPostBodyModules: PropTypes.array.isRequired,
  allFlexColumnModules: PropTypes.object.isRequired,
  allLayoutSectionTrees: PropTypes.object.isRequired,
  globalGroups: PropTypes.object.isRequired,
  globalPartials: PropTypes.object.isRequired,
  headerInfo: PropTypes.object.isRequired,
  footerInfo: PropTypes.object.isRequired,
  widgetsInRichText: PropTypes.instanceOf(ImmutableMap).isRequired,
  allLayoutSectionIds: PropTypes.array.isRequired,
  targetedLayoutSectionId: PropTypes.string,
  currentDragItem: PropTypes.oneOfType([PropTypes.instanceOf(ImmutableMap), PropTypes.object]),
  nodePositionsNeedUpdate: PropTypes.bool.isRequired,
  hoverPositionsFrozen: PropTypes.bool.isRequired,
  layoutIteratorMap: PropTypes.object.isRequired,
  mouseCoordsAtDrop: PropTypes.object.isRequired,
  nodePositionsByTreeId: PropTypes.object,
  currentNodePositions: PropTypes.object,
  nodePositionConfig: PropTypes.object.isRequired,
  calculateNodePositions: PropTypes.func.isRequired,
  calculateHoverPositions: PropTypes.func.isRequired,
  nodePositionsByConfigAndId: PropTypes.object,
  currentBodyOffset: PropTypes.object,
  scrollLeftPosition: PropTypes.number,
  scrollTopPosition: PropTypes.number,
  selectedItemPositionInfo: PropTypes.object,
  currentModuleToolbarInfo: PropTypes.object,
  currentEmptyContainerToolbarInfo: PropTypes.object,
  currentColumnResizeHandleInfo: PropTypes.array.isRequired,
  currentRowToolbarInfo: PropTypes.object,
  currentColumnToolbarInfo: PropTypes.object,
  currentTopmostRowToolbarInfo: PropTypes.object,
  currentInsertNewSectionToolbarInfo: PropTypes.object,
  hoveredDragHandleUpdated: PropTypes.func.isRequired,
  dragHandlesBlurred: PropTypes.func.isRequired,
  layoutSectionHoverPositionsUpdated: PropTypes.func.isRequired,
  layoutSectionNodePositionsUpdated: PropTypes.func.isRequired,
  layoutSectionNodePositionsRefreshedButUnchanged: PropTypes.func.isRequired,
  iframeScrollPositionChanged: PropTypes.func.isRequired,
  previewScrollPositionChanged: PropTypes.func.isRequired,
  mouseEnteredIFrame: PropTypes.func.isRequired,
  mouseLeftIFrame: PropTypes.func.isRequired,
  nodePositionsFrozen: PropTypes.bool.isRequired,
  isDistractionFreeOpen: PropTypes.bool
};
NodePositionContainer.contextType = TourContext;
const convertMouseCoordsToPojo = memoizeOne(mouseCoords => {
  if (!mouseCoords) {
    return undefined;
  }
  return Iterable.isIterable(mouseCoords) ? mouseCoords.toJS() : mouseCoords;
});
const mapStateToProps = state => {
  return {
    allLayoutSectionIds: getAllLayoutSectionIds(state),
    allLayoutSectionTrees: getAllLayoutSectionTreesAsMap(state),
    allStaticModules: getAllStaticModulesToShowOverlaysOn(state),
    allFlexColumnModules: getAllFlexColumnModules(state),
    allPostBodyModules: getModulesFromPostBody(state),
    globalGroups: getGlobalGroupInfo(state),
    globalPartials: getGlobalPartialInfo(state),
    headerInfo: getHeaderInfo(state),
    footerInfo: getFooterInfo(state),
    widgetsInRichText: getWidgetsInRichText(state),
    targetedLayoutSectionId: getTargetedOrNearbyLayoutSectionId(state),
    currentDragItem: getCurrentDragItem(state),
    currentNodePositions: getNodePositions(state),
    nodePositionsByConfigAndId: getNodePositionsByConfigAndId(state),
    nodePositionsByTreeId: getNodePositionsByTreeId(state),
    nodePositionConfig: getNodePositionConfig(state),
    calculateNodePositions: getCalculateNodePositionsFunc(state),
    calculateHoverPositions: getCalculateHoverPositionsCalculator(state),
    currentBodyOffset: getBodyOffsetSelector(state),
    nodePositionsNeedUpdate: getNodePositionsNeedUpdate(state),
    hoverPositionsFrozen: getHoverPositionsFrozen(state),
    layoutIteratorMap: getLayoutIteratorMap(state),
    mouseCoordsAtDrop: convertMouseCoordsToPojo(getMouseCoordsAtDrop(state)),
    scrollLeftPosition: getScrollLeftPosition(state),
    scrollTopPosition: getScrollTopPosition(state),
    selectedItemPositionInfo: getSelectedItemPositionInfo(state),
    currentModuleToolbarInfo: getCurrentModuleToolbarInfo(state),
    currentEmptyContainerToolbarInfo: getCurrentEmptyContainerToolbarInfo(state),
    currentRowToolbarInfo: getCurrentRowToolbarInfo(state),
    currentTopmostRowToolbarInfo: getCurrentTopmostRowToolbarInfo(state),
    currentColumnToolbarInfo: getCurrentColumnToolbarInfo(state),
    currentVerticalStackToolbarInfo: getCurrentVerticalStackToolbarInfo(state),
    currentHorizontalStackToolbarInfo: getCurrentHorizontalStackToolbarInfo(state),
    currentGridTableToolbarInfo: getCurrentGridTableToolbarInfo(state),
    currentColumnResizeHandleInfo: getCurrentColumnResizeHandleInfo(state),
    currentInsertNewSectionToolbarInfo: getCurrentInsertNewSectionToolbarInfo(state),
    nodePositionsFrozen: getNodePositionsAreFrozen(state),
    isDistractionFreeOpen: getIsDistractionFreeOpen(state),
    allGridLayoutIds: getAllGridLayoutIds(state),
    allGridLayouts: getAllGridLayoutTreesAsMap(state),
    gridLayoutIteratorMap: getGridLayoutIteratorMap(state),
    isEditingGridLayouts: getIsEditingGridLayouts(state)
  };
};
const mapDispatchToProps = {
  dragHandlesBlurred,
  hoveredDragHandleUpdated,
  layoutSectionHoverPositionsUpdated,
  layoutSectionNodePositionsUpdated,
  layoutSectionNodePositionsRefreshedButUnchanged,
  mouseEnteredIFrame,
  mouseLeftIFrame,
  iframeScrollPositionChanged,
  previewScrollPositionChanged
};
export default connect(mapStateToProps, mapDispatchToProps)(NodePositionContainer);